import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { Dialog } from '@/components/AppDialog'
import { useCreateEquipmentItem, useOutlets, useUpdateEquipmentItem } from '@/api/hooks'
import type { EquipmentItem } from '@/api/types'
import { ErrorBanner } from '@/components/common'

/**
 * What the store holds, by category.
 *
 * <p>The category is not decoration: a vessel outpass offers what is marked
 * VESSEL and nothing else, so a tray filed under linen never reaches the van.
 * Getting it wrong here is what makes the picker on the outpass wrong.</p>
 */
export const CATEGORIES = [
  { code: 'VESSEL', label: 'Vessels — what goes out on an outpass' },
  { code: 'HALL_ASSET', label: 'Hall assets — handed over with the hall' },
  { code: 'LIVE_COUNTER', label: 'Live counter' },
  { code: 'FURNITURE', label: 'Furniture' },
  { code: 'LINEN', label: 'Linen' },
  { code: 'SIGNAGE', label: 'Signage' },
  { code: 'PACKAGING', label: 'Packaging' },
]

const TYPES = [
  { code: 'REUSABLE', label: 'Reusable — goes out and comes back' },
  { code: 'CONSUMABLE', label: 'Consumable — used up' },
]

const UNITS = ['NOS', 'SET', 'KG', 'PAIR', 'ROLL', 'PACKET']

export function EquipmentItemDialog({
  open,
  item,
  defaultCategory,
  onClose,
}: {
  open: boolean
  /** The item being corrected. Absent, a new one is added to the store. */
  item?: EquipmentItem | null
  defaultCategory?: string
  onClose: () => void
}) {
  const create = useCreateEquipmentItem()
  const update = useUpdateEquipmentItem()
  const { data: outlets } = useOutlets()
  const editing = Boolean(item)

  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [category, setCategory] = useState(defaultCategory ?? 'VESSEL')
  const [equipmentType, setEquipmentType] = useState('REUSABLE')
  const [unit, setUnit] = useState('NOS')
  const [outletId, setOutletId] = useState('')
  const [stockQuantity, setStockQuantity] = useState('0')
  const [unitCost, setUnitCost] = useState('0')
  const [replacementCost, setReplacementCost] = useState('0')

  useEffect(() => {
    if (!open) return
    setCode(item?.code ?? '')
    setName(item?.name ?? '')
    setCategory(item?.category ?? defaultCategory ?? 'VESSEL')
    setEquipmentType(item?.equipmentType ?? 'REUSABLE')
    setUnit(item?.unit ?? 'NOS')
    setOutletId('')
    setStockQuantity(item ? String(item.stockQuantity) : '0')
    setUnitCost(item ? String(item.unitCost) : '0')
    setReplacementCost(item ? String(item.replacementCost) : '0')
  }, [open, item?.id, defaultCategory])

  const submit = () => {
    const body = {
      code: code.trim().toUpperCase(),
      name: name.trim(),
      category,
      equipmentType,
      unit,
      outletId: outletId || undefined,
      stockQuantity: Number(stockQuantity || 0),
      unitCost: Number(unitCost || 0),
      replacementCost: Number(replacementCost || 0),
    }
    if (item) {
      update.mutate({ id: item.id, body }, { onSuccess: onClose })
      return
    }
    create.mutate(body, { onSuccess: onClose })
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editing ? `${item?.name}` : 'Add to the store'}</DialogTitle>
      <DialogContent dividers>
        <ErrorBanner error={create.error ?? update.error} />
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12} md={4}>
            <TextField
              label="Code"
              value={code}
              onChange={(event) => setCode(event.target.value)}
              fullWidth
              required
              placeholder="VSL-BIRYANI-30"
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <TextField
              label="Name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              fullWidth
              required
              placeholder="Biryani vessel, 30 kg"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              select
              label="Category"
              value={category}
              onChange={(event) => setCategory(event.target.value)}
              fullWidth
              helperText="What an outpass offers follows this"
            >
              {CATEGORIES.map((option) => (
                <MenuItem key={option.code} value={option.code}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              select
              label="Kind"
              value={equipmentType}
              onChange={(event) => setEquipmentType(event.target.value)}
              fullWidth
            >
              {TYPES.map((option) => (
                <MenuItem key={option.code} value={option.code}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              select
              label="Unit"
              value={unit}
              onChange={(event) => setUnit(event.target.value)}
              fullWidth
            >
              {UNITS.map((value) => (
                <MenuItem key={value} value={value}>
                  {value}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={8}>
            <TextField
              select
              label="Held at"
              value={outletId}
              onChange={(event) => setOutletId(event.target.value)}
              fullWidth
              helperText="Left blank, it belongs to the business"
            >
              <MenuItem value="">The business</MenuItem>
              {(outlets ?? []).map((outlet) => (
                <MenuItem key={outlet.id} value={outlet.id}>
                  {outlet.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label="In stock"
              type="number"
              value={stockQuantity}
              onChange={(event) => setStockQuantity(event.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Unit cost"
              type="number"
              value={unitCost}
              onChange={(event) => setUnitCost(event.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Replacement cost"
              type="number"
              value={replacementCost}
              onChange={(event) => setReplacementCost(event.target.value)}
              fullWidth
              helperText="Charged when it does not come back"
            />
          </Grid>
        </Grid>

        {editing ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            Moving this out of Vessels takes it off the outpass picker. Passes already raised
            against it are untouched.
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={!code.trim() || !name.trim() || create.isPending || update.isPending}
        >
          {editing ? 'Save' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
